import React, { useState } from 'react';
import {
  ActivityIndicator, Platform, Pressable, StyleSheet,
  Text, TouchableOpacity, View, Keyboard, ScrollView,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import Colors from '../../constants/Colors';
import CloseModalIcon from '../CloseModalIcon';
import createUser from '../../requests/Users/CreateUser';
import { emailRegex } from '../Regex';
import { logEvent } from '../LogEvent';
import isMobile from '../IsMobile';
import PressableStyled from '../PressableStyled';
import Button from '../Button';
import { SEND_INVITE_SUCCESS } from '../../constants/AlertMessages';
import AlertWidget from '../AlertWidget';

interface InviteUserBottomSheetProps {
  users: any;
  invitedUser: () => void;
  roles?: any[];
  close: () => void;
}

const InviteUserBottomSheet = ({
  invitedUser, users, roles = [], close,
}: InviteUserBottomSheetProps) => {
  const [firstNameFocused, setFirstNameFocused] = useState<boolean>(false);
  const [secondNameFocused, setSecondNameFocused] = useState<boolean>(false);
  const [emailFocused, setEmailFocused] = useState<boolean>(false);
  const [descriptionHeight, setDescriptionHeight] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [titleValid, setTitleValid] = useState<boolean>(true);
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [firstNameValue, setFirstNameValue] = useState<string>('');
  const [secondNameValue, setSecondNameValue] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [roleDropdownVisible, setRoleDropdownVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState(null);

  const maxTitleLength: number = 40;

  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');

  const showAlert = (title: string, error: boolean = true, message: string = '') => {
    setAlertTitle(title);
    setAlertError(error);
    setAlertMessage(message);
    setAlertVisible(true);
  };

  const getTestID = (role) => {
    switch (role) {
      case 'Admin':
        return 'um-admin-role-btn';
      case 'Non Admin':
        return 'um-non-admin-role-btn';
      default:
        return 'um-other-role-btn';
    }
  };

  const restoreDefaults: () => void = () => {
    setFirstNameFocused(false);
    setSecondNameFocused(false);
    setEmailFocused(false);
    setDescriptionHeight(0);
    setSaving(false);
    setTitleValid(true);
    setEmailValid(false);
    setFirstNameValue('');
    setSecondNameValue('');
    setEmail('');
    setSelectedRole(null);
    setSelectedRoleName(null);
  };

  function validateTitle(title) {
    let valid = true;
    if (
      title === undefined
      || title === null
      || title?.trim().length === 0
      || title?.trim().length > maxTitleLength
    ) {
      valid = false;
      setTitleValid(false);
    } else {
      setTitleValid(true);
    }
    return valid;
  }

  const validateEmail = (text: string) => {
    const isValid: boolean = text?.length > 0 && new RegExp(emailRegex.exp).test(text);
    setEmailValid(isValid);
    return isValid;
  };

  const save: () => void = () => {
    logEvent('InviteUserModal', 'Send');
    const emails: string[] = users.map((user) => user.email?.trim().toLowerCase());
    if (emails.indexOf(email.trim().toLowerCase()) >= 0) {
      showAlert(
        'Oops! This user is already invited or signed up',
        true,
        'It looks like this user has already been invited or is already a part of our system. Please review the details or let us know if you need help!',
      );
      return;
    }
    if (titleValid && emailValid) {
      setSaving(true);
      const userIds = [];
      const deletedUserIds = [];
      users.forEach((user) => {
        if (user.selected) {
          userIds.push(user.ClientIdEmail);
        } else if (user.deleted) {
          deletedUserIds.push(user.ClientIdEmail);
        }
      });
      createUser(
        email,
        firstNameValue?.trim(),
        secondNameValue?.trim(),
        selectedRole === 'Admin',
        (selectedRole === 'Admin') ? null : selectedRole,
      ).then((res) => {
        if (res?.isError) {
          setSaving(false);
          showAlert('Error', true, res.errorMessage);
        } else {
          setSaving(false);
          restoreDefaults();
          invitedUser();
          showAlert(SEND_INVITE_SUCCESS, false);
        }
      });
    }
  };

  const renderDetails = () => (
    <View style={{ }}>
      <View style={{ }}>
        <View style={{ flex: 1 }}>
          <Text style={styles.textBoxHeaders}>{'First name'}</Text>
          <BottomSheetTextInput
            style={[
              styles.noteText,
              {
                borderColor: firstNameFocused ? Colors.light.hereworksBlue300
                  : Colors.light.hereworksBlue100,
              },
            ]}
            placeholderTextColor={Colors.light.hereworksBlack400}
            placeholder={'First name'}
            onFocus={() => setFirstNameFocused(true)}
            onBlur={() => setFirstNameFocused(false)}
            value={firstNameValue ?? ''}
            onChangeText={(text: string) => {
              validateTitle(text);
              setFirstNameValue(text);
            }}
          />
          {firstNameValue?.trim().length > maxTitleLength && (
            <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
              {`${firstNameValue?.trim().length ?? 0}/${maxTitleLength}`}
            </Text>
          )}
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.textBoxHeaders}>{'Second name'}</Text>
          <BottomSheetTextInput
            style={[
              styles.noteText,
              {
                borderColor: secondNameFocused ? Colors.light.hereworksBlue300
                  : Colors.light.hereworksBlue100,
              },
            ]}
            placeholderTextColor={Colors.light.hereworksBlack400}
            placeholder={'Second name'}
            onFocus={() => setSecondNameFocused(true)}
            onBlur={() => setSecondNameFocused(false)}
            value={secondNameValue ?? ''}
            onChangeText={(text) => {
              validateTitle(text);
              setSecondNameValue(text);
            }}
          />
          {secondNameValue?.trim().length > maxTitleLength && (
            <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
              {`${secondNameValue?.trim().length ?? 0}/${maxTitleLength}`}
            </Text>
          )}
        </View>
      </View>
      <Text style={styles.textBoxHeaders}>{'Email*'}</Text>
      <BottomSheetTextInput
        style={[
          styles.noteText,
          {
            borderColor: emailFocused ? Colors.light.hereworksBlue300
              : Colors.light.hereworksBlue100,
          },
          { minHeight: descriptionHeight },
        ]}
        placeholderTextColor={Colors.light.hereworksBlack400}
        placeholder={'Email'}
        onFocus={() => setEmailFocused(true)}
        onBlur={() => setEmailFocused(false)}
        value={email ?? ''}
        onChangeText={(text) => {
          validateEmail(text);
          setEmail(text);
        }}
        multiline={true}
        scrollEnabled={false}
        onContentSizeChange={(event) => {
          setDescriptionHeight(event.nativeEvent.contentSize.height);
        }}
      />
      <>
        <Text style={styles.textBoxHeaders}>Role*</Text>
        <TouchableOpacity
          style={styles.dropdownBox}
          onPress={() => {
            setRoleDropdownVisible(!roleDropdownVisible);
            Keyboard.dismiss();
            logEvent('EditUser', `Role_dropdown_${roleDropdownVisible ? 'Close' : 'Open'}`);
          }}
        >
          <Text style={styles.dropdownBoxText}>{selectedRoleName ?? 'Choose role'}</Text>
          <View style={{ flex: 1, alignItems: 'flex-end' }}>
            <FontAwesome
              size={20}
              style={{
                color: Colors.light.hereworksLightBlue,
                marginRight: 12,
              }}
              name={roleDropdownVisible ? 'angle-up' : 'angle-down'}
            />
          </View>
        </TouchableOpacity>
        {roleDropdownVisible && (
          <View style={styles.dropdownView}>
            <ScrollView nestedScrollEnabled keyboardShouldPersistTaps='handled'>
              <Pressable
                key={0}
                onPress={() => {
                  setSelectedRole('Admin');
                  setSelectedRoleName('Admin');
                  setRoleDropdownVisible(!roleDropdownVisible);
                  logEvent('EditUser', 'Selected_group');
                }}
                testID={getTestID('Admin')}
              >
                <Text style={styles.dropdownItemText}>Admin</Text>
              </Pressable>
              {roles.map((role, key) => (
                <Pressable
                  key={key}
                  onPress={() => {
                    setSelectedRole(role.id);
                    setSelectedRoleName(role.name);
                    setRoleDropdownVisible(!roleDropdownVisible);
                    logEvent('EditUser', 'Selected_group');
                  }}
                  testID={getTestID(role.name)}
                >
                  <Text style={styles.dropdownItemText}>{role.name}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        )}
      </>
    </View>
  );

  const renderSendCancel = () => <View style={styles.buttonContainer}>
    <Button
      disabled={!(emailValid && selectedRole) || saving}
      title='Invite'
      onPress={save}
      children={saving && <ActivityIndicator style={styles.savingIndicator} />}
      affirmative
      testID='send-btn'
      style={{ alignSelf: 'stretch', marginStart: 0 }}
    />
  </View>;

  const renderModalContent = () => (
    <View
      style={{ flex: isMobile ? undefined : 1, alignSelf: 'stretch', justifyContent: isMobile ? 'flex-start' : 'center' }}>
      <View style={styles.container}>
        <Text style={styles.title}>
          {'Invite User'}
        </Text>
        <Text style={styles.description}>
          {'Fields marked with * are mandatory'}
        </Text>
        <View
          style={{
            alignSelf: 'stretch',
            zIndex: 2,
          }}
        >
          {renderDetails()}
        </View>
        {renderSendCancel()}
        <PressableStyled
          onPress={() => {
            logEvent('InviteUserModal', 'Close_Modal');
            restoreDefaults();
            close();
          }}
          style={{ position: 'absolute', top: 0, right: 16 }}
        >
          <CloseModalIcon />
        </PressableStyled>
      </View>
    </View>
  );

  if (alertVisible) {
    return (
      <AlertWidget
        error={alertError}
        title={alertTitle}
        message={alertMessage}
        onModalHide={() => {
          setAlertVisible(false);
        }}
        cancelAction={() => {
          if (alertTitle === SEND_INVITE_SUCCESS) {
            restoreDefaults();
            close();
          } else {
            setAlertVisible(false);
          }
        }}
      />
    );
  }

  return renderModalContent();
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: isMobile ? 'stretch' : 'center',
    borderRadius: 10,
  },
  noteText: {
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    backgroundColor: '#fff',
    borderWidth: 1,
    alignContent: 'center',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 5,
    verticalAlign: 'top',
    outlineWidth: 0,
  },
  textBoxHeaders: {
    marginTop: 12,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  radioBoxHeaders: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    borderRadius: 10,
    marginTop: 5,
    marginBottom: 2,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
  validationText: {
    marginTop: 4,
    color: Colors.light.hereworksBlack300,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 20,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  buttonContainer: {
    marginVertical: isMobile ? 25 : 40,
    alignSelf: 'stretch',
    zIndex: 1,
  },
  savingIndicator: {
    marginStart: 12,
  },
  title: {
    marginBottom: 16,
    fontSize: 16,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  dropdownView: {
    zIndex: 5,
    maxHeight: 150,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    backgroundColor: '#fff',
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
});

export default InviteUserBottomSheet;
